import React, { useMemo } from "react";
import { groupBy } from "lodash";
import { DisplayPlaidConnection } from "../../plaid/DisplayPlaidConnection/DisplayPlaidConnection";
import { UserFinancialConnection } from "../../../common/dto/user/user-integrations-response.dto";
import { PlaidConnectionWithAccounts } from "../../../common/types/plaidConnection";
import { ConnectionProviderType } from "../../../common/types/financialConnection";
import { StripeConnectionsStatus } from "../../stripe/StripeConnectionsStatus";
import { GustoConnectionsStatus } from "../../gusto/GustoConnectionsStatus";
import { RampConnectionStatus } from "../../ramp/RampConnectionStatus";
import { MercuryConnectionStatus } from "../../mercury/MercuryConnectionStatus";
import {
    ConnectionProvider,
    IntegrationConnection,
} from "../../../common/types/integrationConnection";
import { ProviderIntegrationConnections } from "./ProviderIntegrationConnections";

interface Props {
    connections: UserFinancialConnection[];
    integrationConnections: IntegrationConnection[];
    unfilteredConnectionsLength: number;
}

const connectionProviders = Object.values(ConnectionProvider);

export const FinancialConnectionsSettings: React.FC<Props> = ({
    connections,
    integrationConnections,
    unfilteredConnectionsLength,
}) => {
    const connectionsByType = useMemo(
        () =>
            groupBy(
                connections,
                (connection) => connection.connection.connectionProvider,
            ),
        [connections],
    );

    const integrationConnectionsByProvider = useMemo(
        () =>
            groupBy(
                integrationConnections,
                (connection) => connection.provider,
            ),
        [integrationConnections],
    );

    const idsOfFinancialAccountsConnectedToNativeIntegration = useMemo(() => {
        const paypalIntegrationConnections =
            integrationConnectionsByProvider[ConnectionProvider.PAYPAL];

        if (!paypalIntegrationConnections) {
            return [];
        }

        return paypalIntegrationConnections.flatMap(
            (connection) =>
                connection.integrationAccounts
                    ?.map(
                        (integrationAccount) =>
                            integrationAccount.financialAccountId,
                    )
                    .filter((value) => value !== null) ?? [],
        );
    }, [integrationConnectionsByProvider]);

    return (
        <section>
            {connectionsByType[ConnectionProviderType.PLAID]?.map(
                (userConnection) => (
                    <DisplayPlaidConnection
                        key={userConnection.connection.id}
                        connection={
                            userConnection.connection as PlaidConnectionWithAccounts
                        }
                        numberOfNotVisibleAccounts={
                            userConnection.numberOfNotVisibleAccounts
                        }
                        idsOfFinancialAccountsConnectedToNativeIntegration={
                            idsOfFinancialAccountsConnectedToNativeIntegration
                        }
                    />
                ),
            )}
            {connectionsByType[ConnectionProviderType.STRIPE] && (
                <StripeConnectionsStatus
                    stripeConnections={connectionsByType[
                        ConnectionProviderType.STRIPE
                    ].map((uc) => uc.connection)}
                />
            )}
            {connectionsByType[ConnectionProviderType.RAMP]?.map(
                (userConnection) => (
                    <RampConnectionStatus
                        key={userConnection.connection.id}
                        connection={userConnection.connection}
                    />
                ),
            )}
            {connectionsByType[ConnectionProviderType.MERCURY]?.map(
                (userConnection) => (
                    <MercuryConnectionStatus
                        key={userConnection.connection.id}
                        connection={userConnection.connection}
                    />
                ),
            )}
            {connectionsByType[ConnectionProviderType.GUSTO] && (
                <GustoConnectionsStatus
                    gustoConnections={connectionsByType[
                        ConnectionProviderType.GUSTO
                    ].map((uc) => uc.connection)}
                />
            )}

            {connectionProviders.map(
                (provider) =>
                    integrationConnectionsByProvider[provider] && (
                        <ProviderIntegrationConnections
                            key={provider}
                            provider={provider}
                            integrationConnections={
                                integrationConnectionsByProvider[provider]
                            }
                        />
                    ),
            )}

            {connections.length === 0 &&
                integrationConnections.length === 0 && (
                    <p className="settings__empty-state">
                        {unfilteredConnectionsLength === 0
                            ? "No account connected yet"
                            : "No matching accounts found"}
                    </p>
                )}
        </section>
    );
};
